import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";

import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Typography from "@material-ui/core/Typography";

import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import useNoInteractionClosures from "../../hooks/useNoInteractionClosures";

const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("pt-BR");
};

// Lista os chamados finalizados sem interação de um atendente (ou de todos,
// quando `user` vem sem id) no período selecionado no Dashboard.
const NoInteractionTicketsModal = ({
  open,
  onClose,
  user,
  initialDate,
  finalDate,
}) => {
  const history = useHistory();
  const { find } = useNoInteractionClosures();
  const [loading, setLoading] = useState(false);
  const [details, setDetails] = useState([]);

  useEffect(() => {
    if (!open || !initialDate || !finalDate) return;

    let active = true;
    (async () => {
      setLoading(true);
      try {
        const data = await find({
          initialDate,
          finalDate,
          includeDetails: true,
          ...(user?.userId ? { userId: user.userId } : {}),
        });
        if (active) setDetails(Array.isArray(data?.details) ? data.details : []);
      } catch (err) {
        if (active) {
          setDetails([]);
          toastError(err);
        }
      }
      if (active) setLoading(false);
    })();

    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialDate, finalDate, user?.userId]);

  const handleOpenTicket = (detail) => {
    if (!detail?.uuid) return;
    onClose();
    history.push(`/tickets/${detail.uuid}`);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle>
        {i18n.t("dashboard.noInteraction.detailsTitle")}
        {user?.userName ? ` — ${user.userName}` : ""}
      </DialogTitle>
      <DialogContent dividers>
        {loading && <CircularProgress size={24} />}

        {!loading && details.length === 0 && (
          <Typography>{i18n.t("dashboard.noInteraction.empty")}</Typography>
        )}

        {!loading && details.length > 0 && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{i18n.t("dashboard.noInteraction.table.ticket")}</TableCell>
                <TableCell>{i18n.t("dashboard.noInteraction.table.contact")}</TableCell>
                <TableCell>{i18n.t("dashboard.noInteraction.table.queue")}</TableCell>
                <TableCell>{i18n.t("dashboard.noInteraction.table.connection")}</TableCell>
                <TableCell>{i18n.t("dashboard.noInteraction.table.user")}</TableCell>
                <TableCell>{i18n.t("dashboard.noInteraction.table.closedAt")}</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {details.map((detail) => (
                <TableRow key={detail.id}>
                  <TableCell>#{detail.ticketId}</TableCell>
                  <TableCell>
                    {detail.contactName || "—"}
                    {detail.contactNumber ? ` (${detail.contactNumber})` : ""}
                  </TableCell>
                  <TableCell>{detail.queueName || "—"}</TableCell>
                  <TableCell>{detail.whatsappName || "—"}</TableCell>
                  <TableCell>{detail.userName || "—"}</TableCell>
                  <TableCell>{formatDateTime(detail.closedAt)}</TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      color="primary"
                      disabled={!detail.uuid}
                      onClick={() => handleOpenTicket(detail)}
                    >
                      {i18n.t("dashboard.noInteraction.table.open")}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="default">
          {i18n.t("dashboard.noInteraction.close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NoInteractionTicketsModal;
